import { Box, Divider, Stack, Typography, useTheme } from '@mui/material'
import { decode } from 'html-entities'
import { memo, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { RichTextViewer } from 'src/components/common/RichTextViewer'
import DocumentViewer from 'src/components/common/content/DocumentViewer'
import { IFile } from 'src/generated-types'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor } from 'src/utils'
import { z } from 'zod'
import { CancelButton } from '../../../components/CancelButton'
import { AppForm, AppFormProps, FormSubmitButton, FormTextField } from '../../../components/FormCore'
import { useZodForm } from '../../../hooks/zod-form'
import { StyledEquipmentNameCard } from './ScheduledMaintenanceInformationContainer'
import { IScheduledMaintenance } from 'src/generated-types/scheduled-maintenance'
import { getUTCDate } from 'src/utils/function'

const performMaintenanceSchema = z.object({
  note: z.string(),
  scheduledMaintenanceIds: z.array(z.number()).default([]),
  performedDate: z.string().min(1),
})

const todayDateInputValue = () => new Date().toISOString().slice(0, 10)

const toDateInputValue = (value: Date | string) => {
  if (value instanceof Date) return value.toISOString().slice(0, 10)
  return value.split('T')[0]
}
export interface PerformScheduledMaintenanceFormProps extends AppFormProps {
  maintenanceName: string
  mainComponentSerialNumbers?: string[]
  isPerformAll: boolean
  note?: string
  file?: IFile
  isSkip?: boolean
  performableMaintenances?: IScheduledMaintenance[]
  isCagePerform?: boolean
  cagePositionName?: string
  isEquipmentMaintenaceRowPerform?: boolean
  initialPerformedDate?: string
  isSubmitting?: boolean
}

const MaintenanceNotePreview = memo<{ html: string }>(({ html }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      px: 1.5,
      py: 1.25,
    }}
  >
    <RichTextViewer
      html={html}
      maxHeight={140}
      sx={{
        fontSize: '0.875rem',
        '& .ql-editor': { padding: 0, fontSize: '0.875rem' },
        '& .ql-container.ql-snow': { border: 'none' },
      }}
    />
  </Box>
))

export const PerformMaintenanceForm: React.FC<PerformScheduledMaintenanceFormProps> = ({
  onSubmit,
  onCancel,
  maintenanceName,
  mainComponentSerialNumbers,
  isPerformAll = false,
  isSkip = false,
  note,
  file,
  performableMaintenances,
  isCagePerform,
  cagePositionName,
  isEquipmentMaintenaceRowPerform,
  initialPerformedDate,
  isSubmitting = false,
}) => {
  const { t: translate } = useTranslation(['maintenance', 'common'])
  const theme = useTheme()
  const { openFilePreviewDialog } = useDialogCtx()

  const form = useZodForm(performMaintenanceSchema, {
    defaultValues: {
      performedDate: initialPerformedDate ?? todayDateInputValue(),
      note: '',
    },
  })

  useEffect(() => {
    if (
      !isPerformAll &&
      performableMaintenances &&
      performableMaintenances.length === 1 &&
      new Date(performableMaintenances[0].scheduledDate) < new Date()
    ) {
      form.setValue('performedDate', toDateInputValue(performableMaintenances[0].scheduledDate))
    }
  }, [form, isPerformAll, performableMaintenances])

  const maintenanceNoteHtml = useMemo(() => (note?.trim() ? decode(note) : ''), [note])

  const hasMaintenanceNote = useMemo(() => {
    if (!maintenanceNoteHtml) return false
    const textOnly = maintenanceNoteHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
    return textOnly.length > 0
  }, [maintenanceNoteHtml])

  const message = useMemo(
    () =>
      isPerformAll && !isCagePerform
        ? `${translate('perform-all-related-equipment-type', { ns: 'maintenance' })} ${maintenanceName}. ${translate(
          'below-equipments',
          { ns: 'maintenance' },
        )}`
        : isPerformAll && isCagePerform
          ? `${translate('perform-cage-maintenance', { ns: 'maintenance' })} ${cagePositionName}. ${translate(
            'below-equipments',
            { ns: 'maintenance' },
          )}`
          : isEquipmentMaintenaceRowPerform
            ? `${translate('perform-scheduled-maintenance', { ns: 'maintenance' })} ${maintenanceName}. ${translate(
              'below-equipments',
              { ns: 'maintenance' },
            )}`
            : !isSkip
              ? `${translate('perform-scheduled-maintenance', { ns: 'maintenance' })} ${maintenanceName}.`
              : `${translate('skip-scheduled-maintenance', { ns: 'maintenance' })} ${maintenanceName}.`,
    [
      translate,
      isPerformAll,
      isCagePerform,
      maintenanceName,
      cagePositionName,
      isEquipmentMaintenaceRowPerform,
      isSkip,
    ],
  )

  const maxPerformedDate = todayDateInputValue()

  const handleSubmit = (data: z.infer<typeof performMaintenanceSchema>) => {
    onSubmit({
      ...data,
      performedDate: getUTCDate(new Date(data.performedDate)),
    })
  }

  return (
    <AppForm form={form} onSubmit={handleSubmit}>
      <Stack spacing={2.5} sx={{ px: 1, pt: 0, pb: 2 }}>
        <Box>
          <Typography variant="body1" fontWeight={600}>
            {message}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {translate('are-you-sure-to-proceed', { ns: 'maintenance' })}
          </Typography>
        </Box>

        {(isPerformAll || isEquipmentMaintenaceRowPerform) && mainComponentSerialNumbers && mainComponentSerialNumbers.length > 0 && (
          <Flex.Row sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {mainComponentSerialNumbers.map((serialNumber, index) => (
              <StyledEquipmentNameCard key={index}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: getThemeColor(theme, MUIThemeColors.primaryDark),
                    padding: '0 !important',
                  }}
                >
                  {serialNumber}
                </Typography>
              </StyledEquipmentNameCard>
            ))}
          </Flex.Row>
        )}

        {isPerformAll && performableMaintenances && performableMaintenances.length > 0 && (
          <>
            <Divider />
            <Typography variant="subtitle2" fontWeight={600}>
              {translate('below-maintenance', { ns: 'maintenance' })}
            </Typography>
            <Flex.Row sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
              {performableMaintenances
                .filter((maintenance, index, array) =>
                  array.findIndex(m => m.maintenanceName === maintenance.maintenanceName) === index
                )
                .map((maintenance, index) => (
                  <StyledEquipmentNameCard
                    key={index}
                    isOverdue={new Date(maintenance.scheduledDate).toDateString() < new Date().toDateString()}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: new Date(maintenance.scheduledDate).toDateString() < new Date().toDateString()
                          ? getThemeColor(theme, MUIThemeColors.errorDark)
                          : getThemeColor(theme, MUIThemeColors.primaryDark),
                        padding: '0 !important',
                      }}
                    >
                      {maintenance.maintenanceName}
                    </Typography>
                  </StyledEquipmentNameCard>
                ))}
            </Flex.Row>
          </>
        )}

        <FormTextField
          name="performedDate"
          label={translate('performed-date', { ns: 'maintenance' })}
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: maxPerformedDate }}
          sx={{ p: 0 }}
        />

        {file && (
          <Box sx={{ width: '100%', maxWidth: 280, height: 60 }}>
            <DocumentViewer
              document={true}
              file={file as IFile}
              enablePreview={false}
              disabled={false}
              callbackData={document}
              onFileClick={(e, file) => {
                openFilePreviewDialog(file?.url)
              }}
            />
          </Box>
        )}

        {!isPerformAll && hasMaintenanceNote && (
          <Box>
            <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 0.75 }}>
              {translate('maintenance-note', { ns: 'maintenance' })}
            </Typography>
            <MaintenanceNotePreview html={maintenanceNoteHtml} />
          </Box>
        )}

        <FormTextField
          name="note"
          label={translate('note', { ns: 'maintenance' })}
          multiline
          rows={3}
          fullWidth
          sx={{ p: 0 }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, pt: 0.5 }}>
          <CancelButton onClick={onCancel}>{translate('cancel', { ns: 'common' })}</CancelButton>
          <FormSubmitButton
            createText={isSkip ? translate('abandon', { ns: 'common' }) : translate('perform', { ns: 'maintenance' })}
            isLoading={isSubmitting}
          />
        </Box>
      </Stack>
    </AppForm>
  )
}
